import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  Input,
  Textarea,
  ModalFooter,
  Button,
  useColorMode,
} from "@chakra-ui/react";
import { addDoc, collection, doc, updateDoc, setDoc, arrayUnion, getDoc } from "firebase/firestore";
import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import { FaEdit } from "react-icons/fa";
import { AiOutlinePlus } from "react-icons/ai";
import { db, auth } from "../../firebase/firebase";
import { logToSystem } from "../../utils/systemLogging";
import { IconWrapper } from "../../components/IconWrapper";
import { getTaskPermissions, getProjectScopeAccess } from "../../utils/permissions";
import { showToast } from "../../utils/toastUtils";
type Tasks = {
  id: string;
  title: string;
  description: string;
  createdAt: Date;
  createdBy?: string;
};

interface TaskCreatorProps {
  project: { id: string; name: string };
  channel: string;
  organizationId: string;
  onTaskCreated: (task: {
    id: string;
    channel: string;
    title: string;
    description: string;
    createdAt: string;
  }) => void;
  isOpen: boolean;
  onClose: () => void;
  bg: string;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
  color: string;
  setTasks: Dispatch<SetStateAction<Tasks[]>>;
  isEditing?: boolean; // New prop to differentiate Create vs Update
  selectedTask?: { id: string; title: string; description: string; createdBy?: string } | null;
  currentUser: any;
}

const TaskCreator: React.FC<TaskCreatorProps> = ({
  project,
  channel,
  organizationId,
  onTaskCreated,
  isOpen,
  onClose,
  bg,
  color,
  isEditing = false,
  selectedTask,
  setIsEditing,
  setTasks,
  currentUser,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const [taskTitle, setTaskTitle] = useState(selectedTask?.title || "");
  const [taskDescription, setTaskDescription] = useState(
    selectedTask?.description || ""
  );
  const [isTaskSaving, setIsTaskSaving] = useState(false);

  const taskPerms = React.useMemo(
    () => getTaskPermissions(currentUser),
    [currentUser]
  );
  const { isAdmin, canEdit, canCreate } = taskPerms;

  // A per-project admin/editor grant adds task add/edit rights on top of the
  // org-wide permission.
  const projectScope = React.useMemo(
    () => getProjectScopeAccess(currentUser, project?.id),
    [currentUser, project?.id]
  );
  const canCreateTask = canCreate || projectScope.canManageTasks;
  const canEditTask = React.useCallback(
    (task: any) => canEdit(task) || projectScope.canManageTasks,
    [canEdit, projectScope]
  );

  const isReadOnlyForCurrentUser = React.useMemo(() => {
    if (!isEditing) return false;
    if (!canEditTask(selectedTask || undefined)) return true;
    return false;
  }, [isEditing, canEditTask, selectedTask]);

  const cannotCreate = !isEditing && !canCreateTask;

  const showPermissionToast = () => {
    showToast({
      title: "Permission Restricted",
      description: "You do not have permission to perform this action.",
      status: "warning",
      duration: 3000,
      isClosable: true,
    });
  };

  useEffect(() => {
    if (isEditing && selectedTask) {
      setTaskTitle(selectedTask.title);
      setTaskDescription(selectedTask.description);
    }
  }, [isEditing, selectedTask]);

  // Helper function to log activity
  const logActivity = async (orgId: string, details: string) => {
    try {
      const currentUserAuth = auth.currentUser;
      if (!currentUserAuth) return;

      // Get current user's details from Firestore
      const userRef = doc(db, "users", currentUserAuth.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) return;

      const userData = userSnap.data();

      await logToSystem(orgId, "actions", {
        type: "ACTIVITY",
        user: `${userData.firstName} ${userData.lastName}`,
        email: userData.email,
        action: "Task Management",
        details: details,
        status: "success",
      });
    } catch (error) {
      console.error("Error logging activity:", error);
    }
  };

  const handleClose = () => {
    onClose();
    setTaskTitle("");
    setIsEditing(false);
    setTaskDescription("");
  };

  const handleSaveTask = async () => {
    if (!taskTitle.trim()) {
      showToast({
        title: "Error",
        description: "Task title is required.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (isEditing && !canEditTask(selectedTask || undefined)) {
      showPermissionToast();
      return;
    }
    if (!isEditing && !canCreateTask) {
      showPermissionToast();
      return;
    }
    setIsTaskSaving(true);

    try {
      if (isEditing && selectedTask) {
        // Update existing task in Firestore
        const taskRef = doc(
          db,
          `organization/${organizationId}/projects/${project.id}/channels/${channel}/tasks/${selectedTask.id}`
        );
        await updateDoc(taskRef, {
          title: taskTitle,
          description: taskDescription,
        });
        // Note: Manual state update removed. Parent's onSnapshot listener handles real-time updates.

        // Calculate changes for logging
        const changes: string[] = [];
        if (selectedTask.title !== taskTitle) changes.push(`Title: "${selectedTask.title}" -> "${taskTitle}"`);
        if (selectedTask.description !== taskDescription) changes.push(`Description: "${selectedTask.description}" -> "${taskDescription}"`);
        const changeDetails = changes.length > 0 ? changes.join("; ") : "No changes";

        // Log "Task Group Updated" Activity
        await logActivity(organizationId, `Updated Task Group "${taskTitle}" in project "${project.name}". Changes: ${changeDetails}`);

        setTaskTitle("");
        setTaskDescription("");

        showToast({
          title: "Task Updated",
          description: "The task has been successfully updated.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new task
        const newTask = {
          channel,
          title: taskTitle,
          description: taskDescription,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.id || auth.currentUser?.uid || "",
        };

        const taskRef = collection(
          db,
          `organization/${organizationId}/projects/${project.id}/channels/${channel}/tasks`
        );
        const docRef = await addDoc(taskRef, newTask);
        const taskWithId = { id: docRef.id, ...newTask };

        // Log "Task Group Added" Activity
        await logActivity(organizationId, `Created new Task Group "${taskTitle}" in project "${project.name}"`);

        showToast({
          title: "Success",
          description: "Task created successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        onTaskCreated(taskWithId);
      }

      setTaskTitle("");
      setTaskDescription("");
      onClose();
    } catch (error) {
      console.error("Error saving task:", error);
      showToast({
        title: "Error",
        description: "Failed to save task. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsTaskSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(2px)" />
      <ModalContent
        color={color}
        bg={isDark ? "#1A1C1F" : "white"}
        borderWidth="1px"
        borderColor={isDark ? "#2E2F33" : "gray.200"}
        className="rounded-xl"
        borderRadius="xl"
        boxShadow="lg"
      >
        <ModalHeader className="text-[16px] font-semibold">
          {isEditing ? "Edit Task Group" : "Create Task Group"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Input
              placeholder="Task title"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              className="rounded-lg"
              bg={isDark ? "#14161B" : "white"}
              color={isDark ? "white" : "#111827"}
              borderColor={isDark ? "#2E2F33" : "gray.200"}
              _placeholder={{ color: isDark ? "#C3C9D3" : "gray.500" }}
              focusBorderColor="#B59143"
              _focus={{
                boxShadow: "0 0 0 2px rgba(181,145,67,0.35)",
                borderColor: "#B59143",
              }}
              size="md"
              isReadOnly={isReadOnlyForCurrentUser}
              onClick={() => isReadOnlyForCurrentUser && showPermissionToast()}
            />
            <Textarea
              placeholder="Task description"
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              className="rounded-lg"
              bg={isDark ? "#14161B" : "white"}
              color={isDark ? "white" : "#111827"}
              borderColor={isDark ? "#2E2F33" : "gray.200"}
              _placeholder={{ color: isDark ? "#C3C9D3" : "gray.500" }}
              focusBorderColor="#B59143"
              _focus={{
                boxShadow: "0 0 0 2px rgba(181,145,67,0.35)",
                borderColor: "#B59143",
              }}
              size="md"
              isReadOnly={isReadOnlyForCurrentUser}
              onClick={() => isReadOnlyForCurrentUser && showPermissionToast()}
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          {!cannotCreate && !isReadOnlyForCurrentUser && (
            <Button
              isLoading={isTaskSaving}
              loadingText="Saving..."
              isDisabled={isTaskSaving}
              variant="gold"
              className="px-5"
              onClick={handleSaveTask}
              leftIcon={
                <IconWrapper icon={isEditing ? FaEdit : AiOutlinePlus} size={16} />
              }
            >
              {isEditing ? "Update " : "Save "}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TaskCreator;
